import { useState, useEffect, useRef } from 'react'
import { ArrowLeft, Loader2, Sparkles, BookOpen, ChevronDown, ChevronRight, Calendar, ClipboardList, Users, Layers, ThumbsUp, MessageCircleQuestion, Info, Download, Link, RotateCcw, FileSpreadsheet, Terminal, Trash2, BarChart3, RefreshCw, Copy, Bell, BellOff, PartyPopper, BrainCircuit, AlertTriangle, Settings, Check, FileText, Plus, Eye, X, Send, GitMerge, Cloud } from 'lucide-react'
import { rfpApi, dealsApi } from '@/api/client'
import { Combobox } from '@components/ui/Combobox'
import { Tooltip } from '@components/ui/Tooltip'
import { showToast } from '@/store/uiStore'
import type { RfpProject, RfpRequirement, RfpReferenceDoc, RfpTeamMember, QualificationChecklist, QualYesNo, PresalesRecommendation, RfpStage } from '../types'
import { QUALIFICATION_CRITERIA, EMPTY_CHECKLIST, YES_NO_CONFIG, RECOMMENDATION_CONFIG, RFP_PRODUCTS, RFP_STAGES, SELF_EVAL_PRESETS } from '../types'
import { ExcelUploader } from './ExcelUploader'
import { RequirementsTable } from './RequirementsTable'
import { QualificationGuide } from './QualificationGuide'
import { RfpAiAssist } from './RfpAiAssist'

interface RfpProjectDetailProps {
  projectId: number
  onBack: () => void
}

function unwrap(res: any) {
  return res?.data?.data || res?.data || null
}

function unwrapList(res: any): any[] {
  const d = res?.data?.data || res?.data || []
  return Array.isArray(d) ? d : []
}

export function RfpProjectDetail({ projectId, onBack }: RfpProjectDetailProps) {
  const [project, setProject] = useState<RfpProject | null>(null)
  const [requirements, setRequirements] = useState<RfpRequirement[]>([])
  const [references, setReferences] = useState<RfpReferenceDoc[]>([])
  const [teamMembers, setTeamMembers] = useState<RfpTeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [bulkProcessing, setBulkProcessing] = useState(false)
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 })
  const [bulkPass, setBulkPass] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(1)
  const [runMenuOpen, setRunMenuOpen] = useState(false)
  const [resetMenuOpen, setResetMenuOpen] = useState(false)
  const [selectedResets, setSelectedResets] = useState<Set<string>>(new Set())
  const [resetting, setResetting] = useState(false)
  const [passStatus, setPassStatus] = useState<Record<string, string>>({})
  const [selectedPasses, setSelectedPasses] = useState<Set<string>>(new Set())
  const [showGuide, setShowGuide] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [questionsOpen, setQuestionsOpen] = useState(false)
  const [generatingQuestions, setGeneratingQuestions] = useState(false)
  const [qualQuestions, setQualQuestions] = useState<{ summary: string; groups: Array<{ theme: string; questions: Array<{ question: string; why: string; ask_who: string }> }> } | null>(null)
  const [qualNotes, setQualNotes] = useState('')
  const [promptOpen, setPromptOpen] = useState(false)
  const [promptText, setPromptText] = useState('')
  const [defaultPrompt, setDefaultPrompt] = useState('')
  const [savingPrompt, setSavingPrompt] = useState(false)
  const [dealOptions, setDealOptions] = useState<Array<{ id: number; customer_name: string; hubspot_deal_name?: string }>>([])
  const [dealSearching, setDealSearching] = useState(false)
  const [showDealLink, setShowDealLink] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [statsOpen, setStatsOpen] = useState(false)
  const [logsOpen, setLogsOpen] = useState(false)
  const [questionLogsOpen, setQuestionLogsOpen] = useState(false)
  const [logs, setLogs] = useState<Array<{ id: number; requirement_id: number | null; level: string; pass: string | null; provider: string | null; message: string; duration_ms: number | null; created_at: string }>>([])
  const logsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const abortRef = useRef(false)
  const autoOpenedRef = useRef(false)
  const [slackRemindersOpen, setSlackRemindersOpen] = useState(false)
  const [requirementsOpen, setRequirementsOpen] = useState(true)
  const [slackMessages, setSlackMessages] = useState<Array<{ id: number; message_type: string; slack_ts: string | null; channel_id: string; message_text: string; people_tagged: string | null; pending_counts: string | null; created_at: string }>>([])
  const [remindersLoading, setRemindersLoading] = useState(false)
  const [resendingId, setResendingId] = useState<number | null>(null)
  const [celebrateLoading, setCelebrateLoading] = useState<string | null>(null)
  const [projectDocs, setProjectDocs] = useState<Array<{ id: number; title: string; content_length: number; created_by_name: string | null; created_at: string }>>([])
  const [docsOpen, setDocsOpen] = useState(false)
  const [addingDoc, setAddingDoc] = useState(false)
  const [newDocTitle, setNewDocTitle] = useState('')
  const [newDocContent, setNewDocContent] = useState('')
  const [savingDoc, setSavingDoc] = useState(false)
  const [bgProgress, setBgProgress] = useState<{
    kb: { done: number; total: number }
    brain: { done: number; total: number } | null
    brain_review: { done: number; total: number } | null
    decompose: { done: number; total: number } | null
    consolidate: { done: number; total: number } | null
  } | null>(null)
  const bgPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [viewingDocId, setViewingDocId] = useState<number | null>(null)
  const [viewingDocContent, setViewingDocContent] = useState<string | null>(null)

  const searchDeals = async (query: string) => {
    setDealSearching(true)
    const res = await dealsApi.list({ search: query || undefined, pageSize: 30 })
    if (res.success && res.data) {
      const raw = res.data as any
      const dealData = raw.items || raw.deals || raw.data?.items || raw.data?.deals || []
      if (Array.isArray(dealData)) setDealOptions(dealData)
    }
    setDealSearching(false)
  }

  const handleLinkDeal = async (dealId: string) => {
    if (!project || !dealId) return
    const id = parseInt(dealId)
    await rfpApi.updateProject(projectId, { deal_id: id })
    const deal = dealOptions.find(d => d.id === id)
    setProject({ ...project, deal_id: id, customer_name: deal?.customer_name })
    setShowDealLink(false)
    showToast('Deal linked', 'success')
  }

  const handleUnlinkDeal = async () => {
    if (!project) return
    await rfpApi.updateProject(projectId, { deal_id: null })
    setProject({ ...project, deal_id: null, customer_name: undefined })
    showToast('Deal unlinked', 'success')
  }

  const loadPassStatus = async () => {
    const res = await rfpApi.getPassStatus(projectId)
    const data = (res?.data as any)?.data ?? res?.data
    if (Array.isArray(data)) {
      const map: Record<string, string> = {}
      for (const r of data) map[r.pass] = r.last_run
      setPassStatus(map)
    }
  }

  const loadAll = async () => {
    const [projRes, reqsRes, refsRes, teamRes, docsRes] = await Promise.all([
      rfpApi.getProject(projectId),
      rfpApi.listRequirements(projectId),
      rfpApi.listReferences(projectId),
      rfpApi.listTeam(),
      rfpApi.listProjectDocuments(projectId),
    ])
    loadPassStatus()
    const proj = unwrap(projRes) as RfpProject
    setProject(proj)
    setRequirements(unwrapList(reqsRes) as RfpRequirement[])
    setReferences(unwrapList(refsRes) as RfpReferenceDoc[])
    setTeamMembers(unwrapList(teamRes) as RfpTeamMember[])
    setProjectDocs(unwrapList(docsRes) as typeof projectDocs)
    setLoading(false)

    // Restore saved questions
    if (proj?.generated_questions_json && !qualQuestions) {
      try {
        const saved = typeof proj.generated_questions_json === 'string'
          ? JSON.parse(proj.generated_questions_json)
          : proj.generated_questions_json
        if (saved?.groups) setQualQuestions(saved)
      } catch { /* ignore parse errors */ }
    }

    if (!autoOpenedRef.current && proj) {
      const reqs = unwrapList(reqsRes)
      const cl = proj.qualification_checklist
      const hasProgress = cl && Object.values(cl).some(v => v)
      if (reqs.length === 0 && !hasProgress) {
        setDetailsOpen(true)
      }
      autoOpenedRef.current = true
    }
  }

  const fetchLogs = async () => {
    const res = await rfpApi.getLogs(projectId)
    const data = (res?.data as any)?.data ?? res?.data
    if (Array.isArray(data)) setLogs(data)
  }

  useEffect(() => { loadAll() }, [projectId])

  // Load the default questions prompt and initialise from project
  useEffect(() => {
    rfpApi.getDefaultQuestionsPrompt().then(res => {
      const p = (res?.data as any)?.data?.prompt ?? (res?.data as any)?.prompt ?? ''
      setDefaultPrompt(p)
    })
  }, [])
  useEffect(() => {
    if (project && defaultPrompt) {
      setPromptText(project.questions_prompt || defaultPrompt)
    }
  }, [project?.id, defaultPrompt])

  // Auto-poll logs when panel is open
  useEffect(() => {
    if (logsOpen || questionLogsOpen) {
      fetchLogs()
      logsIntervalRef.current = setInterval(fetchLogs, 3000)
    } else if (logsIntervalRef.current) {
      clearInterval(logsIntervalRef.current)
      logsIntervalRef.current = null
    }
    return () => { if (logsIntervalRef.current) clearInterval(logsIntervalRef.current) }
  }, [logsOpen, questionLogsOpen, projectId])

  // Poll background progress when project is processing but browser isn't driving it.
  // If progress doesn't change across 2 consecutive polls, treat as stale and auto-reset.
  const lastProgressRef = useRef<string>('')
  const staleCountRef = useRef(0)
  useEffect(() => {
    if (project?.status === 'processing' && !bulkProcessing) {
      staleCountRef.current = 0
      lastProgressRef.current = ''
      const poll = async () => {
        const res = await rfpApi.getProgress(projectId)
        const d = (res?.data as any)?.data ?? res?.data
        if (d) {
          if (d.status !== 'processing') {
            setProject(prev => prev ? { ...prev, status: d.status } : prev)
            setBgProgress(null)
            return
          }
          setBgProgress({ kb: d.kb, brain: d.brain, brain_review: d.brain_review, decompose: d.decompose, consolidate: d.consolidate })
          // Detect stale processing — if progress hasn't changed in 2 consecutive polls, auto-reset
          const snapshot = JSON.stringify({ kb: d.kb?.done, brain: d.brain?.done, br: d.brain_review?.done, con: d.consolidate?.done })
          if (snapshot === lastProgressRef.current) {
            staleCountRef.current++
            if (staleCountRef.current >= 2) {
              rfpApi.updateProject(projectId, { status: 'complete' })
              setProject(prev => prev ? { ...prev, status: 'complete' } : prev)
              setBgProgress(null)
            }
          } else {
            staleCountRef.current = 0
          }
          lastProgressRef.current = snapshot
        }
      }
      poll()
      bgPollRef.current = setInterval(poll, 5000)
      return () => { if (bgPollRef.current) { clearInterval(bgPollRef.current); bgPollRef.current = null } }
    } else {
      setBgProgress(null)
      if (bgPollRef.current) { clearInterval(bgPollRef.current); bgPollRef.current = null }
    }
  }, [project?.status, bulkProcessing, projectId]) // eslint-disable-line react-hooks/exhaustive-deps


  const handleChecklistChange = async (checklist: QualificationChecklist) => {
    if (!project) return
    setProject({ ...project, qualification_checklist: checklist })
    await rfpApi.updateProject(projectId, { qualification_checklist: checklist })
  }

  const handleDateChange = async (field: 'question_cutoff_date' | 'response_due_date', value: string | null) => {
    if (!project) return
    setProject({ ...project, [field]: value })
    await rfpApi.updateProject(projectId, { [field]: value })
  }

  const handleStageChange = async (stage: RfpStage) => {
    if (!project) return
    setProject({ ...project, stage })
    await rfpApi.updateProject(projectId, { stage })
  }

  const handleOwnerChange = async (memberId: number | null) => {
    if (!project) return
    setProject({ ...project, owner_id: memberId })
    await rfpApi.updateProject(projectId, { owner_id: memberId })
    const member = teamMembers.find(m => m.id === memberId)
    showToast(member ? `${member.name} set as project owner` : 'Project owner removed', 'success')
  }

  const handlePresalesOwnerChange = async (memberId: number | null) => {
    if (!project) return
    setProject({ ...project, presales_owner_id: memberId })
    await rfpApi.updateProject(projectId, { presales_owner_id: memberId })
    const member = teamMembers.find(m => m.id === memberId)
    showToast(member ? `${member.name} set as pre-sales owner` : 'Pre-sales owner removed', 'success')
  }

  const handleImport = async (
    rows: Array<{ row_index: number; sheet_name?: string; section?: string; requirement_name?: string; requirement_text: string; requirement_level?: string; original_response?: string }>,
    filename: string
  ) => {
    setImporting(true)
    try {
      await rfpApi.updateProject(projectId, { original_filename: filename })
      const res = await rfpApi.importRequirements(projectId, rows)
      if (res.success) {
        showToast(`Imported ${rows.length} requirements`, 'success')
        await loadAll()
      } else {
        showToast(res.error || 'Import failed', 'error')
      }
    } catch (err) {
      showToast('Import failed', 'error')
    }
    setImporting(false)
  }

  const handleGenerateQuestions = async () => {
    setGeneratingQuestions(true)
    try {
      const res = await rfpApi.generateQualificationQuestions(projectId, qualNotes || undefined)
      const data = (res?.data as any)?.data ?? res?.data
      if (data?.groups) {
        setQualQuestions(data)
        setQuestionsOpen(true)
      } else {
        const errMsg = (res as any)?.error || (res?.data as any)?.error || 'Failed to generate questions'
        showToast(errMsg, 'error')
      }
    } catch (err: any) {
      showToast(err?.message || 'Failed to generate questions', 'error')
    }
    setGeneratingQuestions(false)
  }

  const handleRemoveQuestion = async (groupIndex: number, questionIndex: number) => {
    if (!qualQuestions) return
    const updated = structuredClone(qualQuestions)
    if (!updated.groups?.[groupIndex]) return
    updated.groups[groupIndex].questions.splice(questionIndex, 1)
    if (updated.groups[groupIndex].questions.length === 0) updated.groups.splice(groupIndex, 1)
    setQualQuestions(updated)
    await rfpApi.saveQuestions(projectId, updated)
  }

  const handleExportQuestionsCsv = () => {
    if (!qualQuestions) return
    const escapeCsv = (s: string) => `"${s.replace(/"/g, '""')}"`
    const rows = [['Category', 'Question', 'Strategic Rationale']]
    for (const group of qualQuestions.groups) {
      for (const q of group.questions) {
        rows.push([escapeCsv(group.theme), escapeCsv(q.question), escapeCsv(q.why)])
      }
    }
    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `rfp-questions-${project?.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'export'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const stopProcessing = async () => {
    abortRef.current = true
    await rfpApi.updateProject(projectId, { status: 'complete' })
    setProject(prev => prev ? { ...prev, status: 'complete' } : prev)
    setBulkProcessing(false)
    showToast('Processing stopped', 'info')
    await loadAll()
  }

  const runBulkLoop = async (options?: { passes?: ('decompose' | 'p1' | 'p2' | 'p3' | 'brain' | 'brain_review' | 'consolidate')[]; testLimit?: number }) => {
    const passes = options?.passes || [...(project?.question_granularity ? ['decompose' as const] : []), 'p1', 'p2', 'p3', ...(project?.brain_enabled ? ['brain' as const, 'brain_review' as const] : []), ...(project?.consolidate_enabled ? ['consolidate' as const] : [])]
    const testLimit = options?.testLimit || 0
    abortRef.current = false
    setBulkProcessing(true)
    let totalErrors = 0
    const MAX_CONSECUTIVE_ERRORS = 10
    let testProcessed = 0

    // Generic pass runner — handles the loop, error tracking, and test limit
    const runPass = async (
      passNum: 0 | 1 | 2 | 3 | 4 | 5 | 6,
      passLabel: string,
      apiFn: () => Promise<any>,
      opts?: { trackBatchErrors?: boolean }
    ): Promise<boolean> => {
      setBulkPass(passNum)
      setBulkProgress({ done: 0, total: 0 })
      let remaining = 1
      let consecutiveErrors = 0
      let consecutiveBatchErrors = 0
      const applyTestLimit = testLimit > 0 && passNum === 0 // Test limit only gates decompose — downstream passes process all resulting sub-questions
      while (remaining > 0 && !abortRef.current) {
        if (applyTestLimit && testProcessed >= testLimit) {
          return true // Decompose limit reached — continue to next pass
        }
        try {
          const res = await apiFn()
          if (!res.success) {
            console.error(`[RFP Batch ${passLabel}] API error:`, res.error)
            totalErrors++
            consecutiveErrors++
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
              showToast(`${passLabel} paused after ${MAX_CONSECUTIVE_ERRORS} consecutive errors — click Run AI again to resume`, 'error')
              return false
            }
            await new Promise(r => setTimeout(r, 2000))
            continue
          }
          consecutiveErrors = 0
          const d = (res?.data as any)?.data ?? res?.data
          if (!d) break
          if (d.fatal_error) {
            showToast(`${passLabel} stopped — database error: ${d.fatal_error}`, 'error')
            return false
          }
          totalErrors += d.batch_errors || 0
          remaining = d.remaining ?? 0
          const effectiveTotal = applyTestLimit ? Math.min(d.total || 0, testLimit) : (d.total || 0)
          setBulkProgress({ done: Math.min(d.processed || 0, effectiveTotal), total: effectiveTotal })
          if (applyTestLimit) testProcessed += d.batch_processed || 0
          if (opts?.trackBatchErrors && d.batch_errors > 0 && d.batch_processed === 0) {
            consecutiveBatchErrors++
            if (consecutiveBatchErrors >= 5) {
              showToast(`${passLabel} stopped — AI service appears to be down`, 'error')
              return false
            }
          } else {
            consecutiveBatchErrors = 0
          }
          const reqsRes = await rfpApi.listRequirements(projectId)
          setRequirements(unwrapList(reqsRes) as RfpRequirement[])
        } catch (err: any) {
          console.error(`[RFP Batch ${passLabel}] Exception:`, err)
          totalErrors++
          consecutiveErrors++
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            showToast(`${passLabel} paused after ${MAX_CONSECUTIVE_ERRORS} consecutive errors — click Run AI again to resume`, 'error')
            return false
          }
          await new Promise(r => setTimeout(r, 2000))
        }
      }
      return !abortRef.current
    }

    // Run selected passes in order
    if (passes.includes('decompose') && project?.question_granularity) {
      const isDecomposeOnly = passes.length === 1 && passes[0] === 'decompose'
      let firstDecomposeCall = true
      const ok = await runPass(0, 'Decompose', () => {
        const opts = isDecomposeOnly && firstDecomposeCall ? { rerun: true } : undefined
        firstDecomposeCall = false
        return rfpApi.decomposeBatch(projectId, opts)
      })
      if (!ok) { setBulkProcessing(false); setProject(prev => prev ? { ...prev, status: 'complete' } : prev); await rfpApi.updateProject(projectId, { status: 'complete' }); await loadAll(); return }
    }
    if (passes.includes('p1')) {
      const ok = await runPass(1, 'Pass 1: Samples', () => rfpApi.aiSearchBatch(projectId))
      if (!ok) { setBulkProcessing(false); setProject(prev => prev ? { ...prev, status: 'complete' } : prev); await rfpApi.updateProject(projectId, { status: 'complete' }); await loadAll(); return }
    }
    if (passes.includes('p2')) {
      const ok = await runPass(2, 'Pass 2: KB', () => rfpApi.aiSearchBatchKb(projectId), { trackBatchErrors: true })
      if (!ok) { setBulkProcessing(false); setProject(prev => prev ? { ...prev, status: 'complete' } : prev); await rfpApi.updateProject(projectId, { status: 'complete' }); await loadAll(); return }
    }
    if (passes.includes('p3')) {
      const ok = await runPass(3, 'Pass 3: KB (loose)', () => rfpApi.aiSearchBatchKbLoose(projectId), { trackBatchErrors: true })
      if (!ok) { setBulkProcessing(false); setProject(prev => prev ? { ...prev, status: 'complete' } : prev); await rfpApi.updateProject(projectId, { status: 'complete' }); await loadAll(); return }
    }
    if (passes.includes('brain') && project?.brain_enabled) {
      const ok = await runPass(4, 'Brain', () => rfpApi.aiSearchBatchBrain(projectId), { trackBatchErrors: true })
      if (!ok) { setBulkProcessing(false); setProject(prev => prev ? { ...prev, status: 'complete' } : prev); await rfpApi.updateProject(projectId, { status: 'complete' }); await loadAll(); return }
    }
    if (passes.includes('brain_review') && project?.brain_enabled) {
      const ok = await runPass(5, 'Brain Review', () => rfpApi.aiSearchBatchBrainReview(projectId), { trackBatchErrors: true })
      if (!ok) { setBulkProcessing(false); setProject(prev => prev ? { ...prev, status: 'complete' } : prev); await rfpApi.updateProject(projectId, { status: 'complete' }); await loadAll(); return }
    }
    if (passes.includes('consolidate') && project?.consolidate_enabled) {
      const ok = await runPass(6, 'Consolidate', () => rfpApi.consolidateBatch(projectId), { trackBatchErrors: true })
      if (!ok) { setBulkProcessing(false); setProject(prev => prev ? { ...prev, status: 'complete' } : prev); await rfpApi.updateProject(projectId, { status: 'complete' }); await loadAll(); return }
    }
    await rfpApi.updateProject(projectId, { status: 'complete' })
    setProject(prev => prev ? { ...prev, status: 'complete' } : prev)
    setBulkProcessing(false)
    if (totalErrors > 0) {
      showToast('AI search finished with some errors', 'warning')
    } else {
      showToast('AI search complete', 'success')
    }
    await loadAll()
  }

  const handleExportToSheets = async () => {
    setExporting(true)
    try {
      const res = await rfpApi.exportToSheets(projectId)
      if (res.success && res.data) {
        const d = (res.data as any)?.data ?? res.data
        showToast(`Exported ${d.row_count} rows to Google Sheets`, 'success')
        window.open(d.sheet_url, '_blank')
      } else {
        showToast(res.error || 'Export failed', 'error')
      }
    } catch {
      showToast('Export failed', 'error')
    } finally {
      setExporting(false)
    }
  }

  const startRun = async (options?: { passes?: ('decompose' | 'p1' | 'p2' | 'p3' | 'brain' | 'brain_review' | 'consolidate')[]; testLimit?: number }) => {
    await rfpApi.updateProject(projectId, { status: 'processing' })
    setProject(prev => prev ? { ...prev, status: 'processing' } : prev)
    setBulkProgress({ done: 0, total: 0 })
    setRunMenuOpen(false)
    runBulkLoop(options)
  }


  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-[var(--text-muted)]" /></div>
  }

  if (!project) {
    return <p className="text-sm text-red-400">Project not found</p>
  }

  const cl = { ...EMPTY_CHECKLIST, ...project.qualification_checklist }

  // Progress calculation
  const allCriteriaAnswered = QUALIFICATION_CRITERIA.every(c => cl[c.key] !== null)
  const hasCompetitors = cl.competitors.trim().length > 0
  const hasAsIs = cl.current_vendor.trim().length > 0 || cl.current_solution.trim().length > 0
  const hasRecommendation = cl.presales_recommendation !== null

  const progressItems = [
    { done: !!project.question_cutoff_date },
    { done: !!project.response_due_date },
    { done: allCriteriaAnswered },
    { done: hasCompetitors && hasAsIs },
    { done: hasRecommendation },
    { done: !!project.original_filename },
  ]
  const progressCount = progressItems.filter(p => p.done).length
  const progressPercent = Math.round((progressCount / progressItems.length) * 100)

  // Inline date summaries for collapsed view
  const formatDate = (d: string | null) => {
    if (!d) return null
    return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-3"
        >
          <ArrowLeft size={14} />
          Back to RFP Projects
        </button>

        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-[var(--text-primary)] truncate">{project.title}</h2>
              {project.total_requirements > 0 && (
                <span className="text-xs text-[var(--text-muted)] shrink-0">{project.total_requirements} requirements</span>
              )}
            </div>
            {/* Deal link — compact row */}
            <div className="flex items-center gap-2 mt-1">
              {project.deal_id ? (
                <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                  <Link size={10} className="text-indigo-400" />
                  <span className="font-medium text-[var(--text-secondary)]">{project.customer_name || `Deal #${project.deal_id}`}</span>
                  <button onClick={handleUnlinkDeal} className="text-[var(--text-muted)] hover:text-red-400 transition-colors text-[10px]">unlink</button>
                </span>
              ) : showDealLink ? (
                <div className="flex items-center gap-2">
                  <Combobox
                    options={dealOptions.map(d => ({
                      value: String(d.id),
                      label: `${d.customer_name}${d.hubspot_deal_name ? ` — ${d.hubspot_deal_name}` : ''}`,
                    }))}
                    value=""
                    onChange={(val) => handleLinkDeal(val)}
                    onSearch={searchDeals}
                    searching={dealSearching}
                    placeholder="Search deals..."
                    inputClassName="w-64 rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] px-2 py-1 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-indigo-500"
                  />
                  <button onClick={() => setShowDealLink(false)} className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)]">cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => setShowDealLink(true)}
                  className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  <Link size={10} />
                  Link to deal
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => {
                const slug = project.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                const url = `${window.location.origin}/dashboard/presales/tools/rfp-tool?rfp=${slug}`
                navigator.clipboard.writeText(url)
                showToast('Link copied to clipboard', 'success')
              }}
              className="p-2 rounded-lg border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              title="Copy link"
            >
              <Link size={14} />
            </button>
            <button
              onClick={() => setShowGuide(true)}
              className="p-2 rounded-lg border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              title="Qualification Guide"
            >
              <BookOpen size={14} />
            </button>
            {requirements.length > 0 && references.length > 0 && (
              (bulkProcessing || project?.status === 'processing') ? (
                <div className="flex items-center gap-1.5">
                  {bulkProcessing && (
                    <span className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--text-muted)]">
                      <Loader2 size={12} className="animate-spin text-violet-400" />
                      {(() => {
                        const passLabel = bulkPass === 0 ? 'Decompose' : bulkPass === 1 ? 'Pass 1: Samples' : bulkPass === 2 ? 'Pass 2: KB' : bulkPass === 3 ? 'Pass 3: KB (loose)' : bulkPass === 4 ? 'Brain' : 'Brain Review'
                        return bulkProgress.total === 0
                          ? `${passLabel} — starting...`
                          : `${passLabel} — ${bulkProgress.done}/${bulkProgress.total}`
                      })()}
                    </span>
                  )}
                  {!bulkProcessing && project?.status === 'processing' && (
                    bgProgress ? (
                      <div className="flex items-center gap-3 px-3 py-2">
                        <Cloud size={12} className="text-emerald-400 shrink-0" />
                        {bgProgress.decompose && bgProgress.decompose.done < bgProgress.decompose.total && (
                          <span className="flex items-center gap-1 text-[10px] text-amber-400">
                            <Loader2 size={10} className="animate-spin" />
                            Decompose {bgProgress.decompose.done}/{bgProgress.decompose.total}
                          </span>
                        )}
                        {bgProgress.kb.total > 0 && (
                          <span className={`flex items-center gap-1 text-[10px] ${bgProgress.kb.done >= bgProgress.kb.total ? 'text-emerald-400' : 'text-blue-400'}`}>
                            {bgProgress.kb.done < bgProgress.kb.total ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                            KB {bgProgress.kb.done}/{bgProgress.kb.total}
                          </span>
                        )}
                        {bgProgress.brain && bgProgress.brain.total > 0 && (
                          <span className={`flex items-center gap-1 text-[10px] ${bgProgress.brain.done >= bgProgress.brain.total ? 'text-emerald-400' : 'text-pink-400'}`}>
                            {bgProgress.brain.done < bgProgress.brain.total ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                            Brain {bgProgress.brain.done}/{bgProgress.brain.total}
                          </span>
                        )}
                        {bgProgress.brain_review && bgProgress.brain_review.total > 0 && bgProgress.kb.done >= bgProgress.kb.total && bgProgress.brain && bgProgress.brain.done >= bgProgress.brain.total && (
                          <span className={`flex items-center gap-1 text-[10px] ${bgProgress.brain_review.done >= bgProgress.brain_review.total ? 'text-emerald-400' : 'text-emerald-400/60'}`}>
                            {bgProgress.brain_review.done < bgProgress.brain_review.total ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                            Review {bgProgress.brain_review.done}/{bgProgress.brain_review.total}
                          </span>
                        )}
                        {bgProgress.consolidate && bgProgress.consolidate.total > 0 && bgProgress.brain_review && bgProgress.brain_review.done >= bgProgress.brain_review.total && (
                          <span className={`flex items-center gap-1 text-[10px] ${bgProgress.consolidate.done >= bgProgress.consolidate.total ? 'text-emerald-400' : 'text-orange-400'}`}>
                            {bgProgress.consolidate.done < bgProgress.consolidate.total ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                            Consolidate {bgProgress.consolidate.done}/{bgProgress.consolidate.total}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="flex items-center gap-1.5 px-3 py-2 text-xs text-[var(--text-muted)]">
                        <Loader2 size={12} className="animate-spin text-violet-400" />
                        Processing...
                      </span>
                    )
                  )}
                  <button
                    onClick={stopProcessing}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Stop processing"
                  >
                    <X size={12} />
                    Stop
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                <div className="relative">
                  <button
                    onClick={() => { setRunMenuOpen(v => !v); setResetMenuOpen(false); if (!runMenuOpen) loadPassStatus() }}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors"
                  >
                    <Sparkles size={12} />
                    Run AI
                    <ChevronDown size={12} />
                  </button>
                  {runMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setRunMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-1 z-50 w-80 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl py-2">
                        <p className="px-3 pb-1.5 text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Select Passes</p>
                        {(() => {
                          const allPasses: { key: string; label: string; icon: React.ReactNode; show: boolean }[] = [
                            { key: 'decompose', label: 'Decompose', icon: <Layers size={12} className="text-amber-400" />, show: !!project?.question_granularity },
                            { key: 'p1', label: 'Pass 1: Samples', icon: <Sparkles size={12} className="text-violet-400" />, show: true },
                            { key: 'p2', label: 'Pass 2: KB', icon: <Sparkles size={12} className="text-blue-400" />, show: true },
                            { key: 'p3', label: 'Pass 3: KB (loose)', icon: <Sparkles size={12} className="text-cyan-400" />, show: true },
                            { key: 'brain', label: 'Brain', icon: <BrainCircuit size={12} className="text-pink-400" />, show: !!project?.brain_enabled },
                            { key: 'brain_review', label: 'Brain Review', icon: <Eye size={12} className="text-emerald-400" />, show: !!project?.brain_enabled },
                            { key: 'consolidate', label: 'Consolidate', icon: <GitMerge size={12} className="text-orange-400" />, show: !!project?.consolidate_enabled },
                          ]
                          const visible = allPasses.filter(p => p.show)
                          const passKeyMap: Record<string, string> = { decompose: 'decompose', p1: 'pass1', p2: 'pass2', p3: 'pass3', brain: 'brain', brain_review: 'brain_review', consolidate: 'consolidate' }
                          const formatTime = (iso: string) => {
                            const d = new Date(iso + 'Z')
                            const now = new Date()
                            const diffMs = now.getTime() - d.getTime()
                            const diffMins = Math.floor(diffMs / 60000)
                            if (diffMins < 1) return 'just now'
                            if (diffMins < 60) return `${diffMins}m ago`
                            const diffHours = Math.floor(diffMins / 60)
                            if (diffHours < 24) return `${diffHours}h ago`
                            const diffDays = Math.floor(diffHours / 24)
                            return `${diffDays}d ago`
                          }
                          const allSelected = visible.every(p => selectedPasses.has(p.key))
                          return (
                            <>
                              <button
                                onClick={() => {
                                  if (allSelected) {
                                    setSelectedPasses(new Set())
                                  } else {
                                    setSelectedPasses(new Set(visible.map(p => p.key)))
                                  }
                                }}
                                className="w-full text-left px-3 py-1.5 text-[10px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                              >
                                {allSelected ? 'Deselect all' : 'Select all'}
                              </button>
                              {visible.map(pass => {
                                const logKey = passKeyMap[pass.key] || pass.key
                                const lastRun = passStatus[logKey]
                                return (
                                  <button
                                    key={pass.key}
                                    onClick={() => setSelectedPasses(prev => {
                                      const next = new Set(prev)
                                      if (next.has(pass.key)) next.delete(pass.key)
                                      else next.add(pass.key)
                                      return next
                                    })}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-tertiary)] flex items-center gap-2.5 transition-colors"
                                  >
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedPasses.has(pass.key) ? 'bg-violet-600 border-violet-600' : 'border-[var(--border-color)]'}`}>
                                      {selectedPasses.has(pass.key) && <Check size={10} className="text-white" />}
                                    </div>
                                    {pass.icon}
                                    <span className="text-[var(--text-primary)] flex-1">{pass.label}</span>
                                    {lastRun ? (
                                      <span className="text-[10px] text-[var(--text-muted)]">{formatTime(lastRun)}</span>
                                    ) : (
                                      <span className="text-[10px] text-[var(--text-muted)] italic">never</span>
                                    )}
                                  </button>
                                )
                              })}
                              <div className="border-t border-[var(--border-color)] mt-2 pt-2 px-3 flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      setRunMenuOpen(false)
                                      const passes = Array.from(selectedPasses) as ('decompose' | 'p1' | 'p2' | 'p3' | 'brain' | 'brain_review' | 'consolidate')[]
                                      if (passes.length === 0) { showToast('Select at least one pass', 'error'); return }
                                      startRun({ passes })
                                    }}
                                    disabled={selectedPasses.size === 0}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Sparkles size={12} />
                                    Run {selectedPasses.size > 0 ? `(${selectedPasses.size})` : ''}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setRunMenuOpen(false)
                                      const passes = Array.from(selectedPasses) as ('decompose' | 'p1' | 'p2' | 'p3' | 'brain' | 'brain_review' | 'consolidate')[]
                                      if (passes.length === 0) { showToast('Select at least one pass', 'error'); return }
                                      startRun({ passes, testLimit: 20 })
                                    }}
                                    disabled={selectedPasses.size === 0}
                                    className="flex items-center gap-1 px-2 py-2 text-[10px] font-medium rounded-lg border border-violet-500/30 text-violet-400 hover:bg-violet-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Run selected passes on first 20 items only"
                                  >
                                    <Terminal size={10} />
                                    Test (20)
                                  </button>
                                </div>
                                <button
                                  onClick={async () => {
                                    setRunMenuOpen(false)
                                    const passes = Array.from(selectedPasses) as ('decompose' | 'p1' | 'p2' | 'p3' | 'brain' | 'brain_review' | 'consolidate')[]
                                    if (passes.length === 0) { showToast('Select at least one pass', 'error'); return }
                                    const res = await rfpApi.runOnServer(projectId, passes)
                                    if (res.success) {
                                      const d = (res.data as any)?.data ?? res.data
                                      setProject(prev => prev ? { ...prev, status: 'processing' } : prev)
                                      showToast(`Running in background: ${d.passes?.join(', ') || passes.join(', ')}. You can close this tab.`, 'success')
                                    } else {
                                      showToast(res.error || 'Failed to start background run', 'error')
                                    }
                                  }}
                                  disabled={selectedPasses.size === 0}
                                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Run on server via GitHub Actions — safe to close your laptop"
                                >
                                  <Cloud size={12} />
                                  Run in Background
                                </button>
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    </>
                  )}
                </div>
                {requirements.some(r => r.ai_response || r.brain_response) && (
                <div className="relative">
                  <button
                    onClick={() => { setResetMenuOpen(v => !v); setRunMenuOpen(false) }}
                    className="flex items-center gap-1.5 p-2 text-xs rounded-lg border border-[var(--border-color)] text-[var(--text-muted)] hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/5 transition-colors"
                    title="Reset responses"
                  >
                    <RotateCcw size={14} />
                  </button>
                  {resetMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setResetMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-1 z-50 w-72 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl py-2">
                        <p className="px-3 pb-1.5 text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Reset Responses</p>
                        <p className="px-3 pb-2 text-[10px] text-[var(--text-muted)]">Select what to clear. This cannot be undone.</p>
                        {(() => {
                          const resetOptions: { key: string; label: string; description: string; show: boolean }[] = [
                            { key: 'consolidate', label: 'Consolidated', description: 'Clears merged parent responses only', show: !!project?.consolidate_enabled && requirements.some(r => !r.parent_requirement_id && r.final_response) },
                            { key: 'brain_review', label: 'Brain Review', description: 'Clears review verdicts only', show: !!project?.brain_enabled && requirements.some(r => r.brain_review_response) },
                            { key: 'brain', label: 'Brain Responses', description: 'Clears Brain + Brain Review', show: !!project?.brain_enabled && requirements.some(r => r.brain_response) },
                            { key: 'ai', label: 'AI Responses', description: 'Clears all AI drafts, gap notes, fit & confidence', show: requirements.some(r => r.ai_response) },
                            { key: 'everything', label: 'Everything', description: 'Resets all responses, assignments, and statuses', show: true },
                          ]
                          const visible = resetOptions.filter(o => o.show)
                          return (
                            <>
                              {visible.map(opt => (
                                <button
                                  key={opt.key}
                                  onClick={() => setSelectedResets(prev => {
                                    const next = new Set(prev)
                                    if (opt.key === 'everything') {
                                      // Everything is exclusive — toggle it alone
                                      return next.has('everything') ? new Set() : new Set(['everything'])
                                    }
                                    next.delete('everything')
                                    if (next.has(opt.key)) next.delete(opt.key)
                                    else next.add(opt.key)
                                    return next
                                  })}
                                  className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-tertiary)] flex items-center gap-2.5 transition-colors"
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedResets.has(opt.key) ? 'bg-red-500 border-red-500' : 'border-[var(--border-color)]'}`}>
                                    {selectedResets.has(opt.key) && <Check size={10} className="text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className={`block ${opt.key === 'everything' ? 'text-red-400 font-medium' : 'text-[var(--text-primary)]'}`}>{opt.label}</span>
                                    <span className="block text-[10px] text-[var(--text-muted)]">{opt.description}</span>
                                  </div>
                                </button>
                              ))}
                              <div className="border-t border-[var(--border-color)] mt-2 pt-2 px-3">
                                <button
                                  onClick={async () => {
                                    if (selectedResets.size === 0) { showToast('Select what to reset', 'error'); return }
                                    const labels = Array.from(selectedResets).map(k => resetOptions.find(o => o.key === k)?.label || k).join(', ')
                                    if (!confirm(`Are you sure you want to reset: ${labels}?\n\nThis cannot be undone.`)) return
                                    setResetting(true)
                                    try {
                                      if (selectedResets.has('everything')) {
                                        await rfpApi.resetRequirements(projectId)
                                        showToast('All responses reset', 'success')
                                      } else {
                                        if (selectedResets.has('ai')) {
                                          await rfpApi.resetRequirements(projectId)
                                          showToast('AI responses reset', 'success')
                                        }
                                        if (selectedResets.has('brain')) {
                                          await rfpApi.resetBrainResponses(projectId)
                                          showToast('Brain responses reset', 'success')
                                        }
                                        if (selectedResets.has('brain_review') && !selectedResets.has('brain')) {
                                          await rfpApi.resetBrainReviewResponses(projectId)
                                          showToast('Brain Review responses reset', 'success')
                                        }
                                        if (selectedResets.has('consolidate')) {
                                          await rfpApi.resetConsolidateResponses(projectId)
                                          showToast('Consolidated responses reset', 'success')
                                        }
                                      }
                                      loadAll()
                                    } catch {
                                      showToast('Reset failed', 'error')
                                    }
                                    setResetting(false)
                                    setResetMenuOpen(false)
                                    setSelectedResets(new Set())
                                  }}
                                  disabled={selectedResets.size === 0 || resetting}
                                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {resetting ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                                  {resetting ? 'Resetting...' : `Reset ${selectedResets.size > 0 ? `(${selectedResets.size})` : ''}`}
                                </button>
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    </>
                  )}
                </div>
                )}
                </div>
              )
            )}
            {requirements.length > 0 && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleExportToSheets}
                  disabled={exporting}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50"
                  title="Export to Google Sheets"
                >
                  {exporting ? (
                    <><Loader2 size={12} className="animate-spin" /> Exporting...</>
                  ) : (
                    <><FileSpreadsheet size={12} /> Export</>
                  )}
                </button>
                {project.sheet_url && (
                  <button
                    onClick={async () => {
                      setSyncing(true)
                      try {
                        const res = await rfpApi.syncFromSheets(projectId)
                        if (res.success) {
                          const d = (res.data as any)?.data ?? res.data
                          showToast(`Synced ${d.updated} of ${d.total_rows} rows from Google Sheets`, 'success')
                          await loadAll()
                        } else {
                          showToast(res.error || 'Sync failed', 'error')
                        }
                      } catch {
                        showToast('Sync failed', 'error')
                      } finally {
                        setSyncing(false)
                      }
                    }}
                    disabled={syncing}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50"
                    title="Sync review statuses back from Google Sheets"
                  >
                    {syncing ? (
                      <><Loader2 size={12} className="animate-spin" /> Syncing...</>
                    ) : (
                      <><RefreshCw size={12} /> Sync from Sheet</>
                    )}
                  </button>
                )}
                {project.last_synced_at && (
                  <span className="text-[10px] text-[var(--text-muted)]">
                    Last synced {new Date(project.last_synced_at + 'Z').toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stage pipeline */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mr-1">Stage</span>
        {RFP_STAGES.map((s, i) => {
          const currentStage = project.stage || 'pre_questions'
          const current = currentStage === s.key
          const stageIdx = RFP_STAGES.findIndex(st => st.key === currentStage)
          const past = i < stageIdx

          return (
            <button
              key={s.key}
              onClick={() => handleStageChange(s.key)}
              className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all ${
                current
                  ? `${s.bg} ${s.color} ring-1 ring-current/30`
                  : past
                    ? 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
              }`}
            >
              {current && <span className="inline-block w-1 h-1 rounded-full bg-current mr-1 align-middle" />}
              {s.label}
            </button>
          )
        })}
      </div>

      {/* Response Stats — collapsible */}
      {requirements.length > 0 && (() => {
        // Exclude parent requirements that were decomposed into sub-questions
        const parentIds = new Set<number>()
        for (const r of requirements) { if (r.parent_requirement_id) parentIds.add(r.parent_requirement_id) }
        const statsReqs = requirements.filter(r => !parentIds.has(r.id))
        const total = statsReqs.length
        const withDraft = statsReqs.filter(r => (r.ai_response && r.ai_response.trim().length > 0) || (r.brain_response && r.brain_response.trim().length > 0)).length
        const noDraft = total - withDraft
        const accepted = statsReqs.filter(r => r.status === 'approved' || r.status === 'auto_accepted').length
        const rejected = statsReqs.filter(r => r.status === 'flagged').length
        const awaitingReview = withDraft - accepted - rejected

        // Per-assignee breakdown
        const byAssignee = new Map<string, { total: number; drafted: number; accepted: number; rejected: number; awaitingReview: number }>()
        for (const r of statsReqs) {
          const name = r.assigned_to_name || 'Unassigned'
          if (!byAssignee.has(name)) byAssignee.set(name, { total: 0, drafted: 0, accepted: 0, rejected: 0, awaitingReview: 0 })
          const a = byAssignee.get(name)!
          a.total++
          const hasDraft = (r.ai_response && r.ai_response.trim().length > 0) || (r.brain_response && r.brain_response.trim().length > 0)
          if (hasDraft) a.drafted++
          if (r.status === 'approved' || r.status === 'auto_accepted') a.accepted++
          else if (r.status === 'flagged') a.rejected++
          else if (hasDraft) a.awaitingReview++
        }

        // Per-fit breakdown
        const byFit = { strong: 0, weak: 0, neutral: 0, no_fit: 0, tbd: 0 }
        for (const r of statsReqs) byFit[r.fit]++

        return (
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
            <button
              onClick={() => setStatsOpen(!statsOpen)}
              className="flex items-center justify-between w-full px-5 py-3.5 hover:bg-[var(--bg-tertiary)] transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                {statsOpen ? <ChevronDown size={15} className="text-[var(--text-muted)]" /> : <ChevronRight size={15} className="text-[var(--text-muted)]" />}
                <BarChart3 size={15} className="text-blue-400" />
                <span className="text-sm font-semibold text-[var(--text-primary)]">Response Stats</span>
              </div>
              <div className="flex items-center gap-4 text-[11px] text-[var(--text-muted)]">
                <span><strong className="text-emerald-400">{accepted}</strong>/{total} accepted</span>
                {awaitingReview > 0 && <span className="text-blue-400">{awaitingReview} awaiting review</span>}
                {noDraft > 0 && <span className="text-red-400">{noDraft} no draft</span>}
              </div>
            </button>

            {statsOpen && (
              <div className="border-t border-[var(--border-color)] p-5 space-y-5">
                {/* Overview */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { label: 'Total', value: total, color: 'text-[var(--text-primary)]' },
                    { label: 'AI Drafted', value: withDraft, color: 'text-blue-400' },
                    { label: 'Awaiting Review', value: awaitingReview, color: 'text-amber-400' },
                    { label: 'Accepted', value: accepted, color: 'text-emerald-400' },
                    { label: 'Rejected', value: rejected, color: 'text-red-400' },
                  ].map(s => (
                    <div key={s.label} className="rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] px-3 py-2.5 text-center">
                      <div className={`text-lg font-bold tabular-nums ${s.color}`}>{s.value}</div>
                      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Per-assignee table */}
                <div>
                  <h4 className="text-xs font-semibold text-[var(--text-secondary)] mb-2">By Assignee</h4>
                  <div className="rounded-lg border border-[var(--border-color)] overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[var(--bg-primary)] text-[var(--text-muted)]">
                          <th className="text-left px-3 py-2 font-medium">Person</th>
                          <th className="text-center px-3 py-2 font-medium">Assigned</th>
                          <th className="text-center px-3 py-2 font-medium">AI Drafted</th>
                          <th className="text-center px-3 py-2 font-medium">Accepted</th>
                          <th className="text-center px-3 py-2 font-medium">Rejected</th>
                          <th className="text-center px-3 py-2 font-medium">Awaiting Review</th>
                          <th className="px-3 py-2 font-medium">Accepted %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from(byAssignee.entries())
                          .sort((a, b) => b[1].total - a[1].total)
                          .map(([name, stats]) => {
                            const pct = stats.total > 0 ? Math.round((stats.accepted / stats.total) * 100) : 0
                            return (
                              <tr key={name} className="border-t border-[var(--border-color)]">
                                <td className="px-3 py-2 text-[var(--text-primary)] font-medium">{name}</td>
                                <td className="px-3 py-2 text-center tabular-nums">{stats.total}</td>
                                <td className="px-3 py-2 text-center tabular-nums text-blue-400">{stats.drafted}</td>
                                <td className="px-3 py-2 text-center tabular-nums text-emerald-400">{stats.accepted}</td>
                                <td className="px-3 py-2 text-center tabular-nums text-red-400">{stats.rejected || ''}</td>
                                <td className="px-3 py-2 text-center tabular-nums text-amber-400">{stats.awaitingReview}</td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden">
                                      <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-[10px] tabular-nums text-[var(--text-muted)] w-8 text-right">{pct}%</span>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Fit distribution */}
                <div>
                  <h4 className="text-xs font-semibold text-[var(--text-secondary)] mb-2">Fit Distribution</h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    {([
                      { key: 'strong' as const, label: 'Strong Fit', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
                      { key: 'weak' as const, label: 'Weak Fit', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
                      { key: 'neutral' as const, label: 'Neutral', color: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
                      { key: 'no_fit' as const, label: 'No Fit', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
                      { key: 'tbd' as const, label: 'TBD', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
                    ]).filter(f => byFit[f.key] > 0).map(f => (
                      <span key={f.key} className={`px-2.5 py-1 rounded-md text-xs font-medium border ${f.color}`}>
                        {f.label}: {byFit[f.key]}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Copy Slack message */}
                <div className="pt-3 border-t border-[var(--border-color)]">
                  <button
                    onClick={() => {
                      const title = project.title || 'RFP'
                      const dueDate = project.response_due_date
                        ? new Date(project.response_due_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })
                        : null
                      const sheetUrl = project.sheet_url || '(no sheet exported yet)'

                      // Build per-assignee summary (only people with items awaiting review)
                      const assigneeSummary = Array.from(byAssignee.entries())
                        .filter(([name, s]) => name !== 'Unassigned' && s.awaitingReview > 0)
                        .sort((a, b) => b[1].awaitingReview - a[1].awaitingReview)
                        .map(([name, s]) => `  • ${name}: ${s.awaitingReview} response${s.awaitingReview !== 1 ? 's' : ''} to review`)
                        .join('\n')

                      const lines = [
                        `📋 *${title}*${dueDate ? ` — due ${dueDate}` : ''}`,
                        '',
                        `Hey team! 👋 We need your help reviewing AI-drafted responses for this RFP.`,
                        '',
                        `🔗 *Spreadsheet:* ${sheetUrl}`,
                      ]

                      if (assigneeSummary) {
                        lines.push('', `👤 *Responses needing review:*`, assigneeSummary)
                      }

                      lines.push(
                        '',
                        `✅ *What to do:*`,
                        `1. Read the *AI Response* column for your assigned rows`,
                        `2. Mark the *Review Status* as Accepted if you're happy with it`,
                        `3. Otherwise, edit the *Final Response* column until you are, then mark Accepted`,
                        `4. If you need to reassign, change the *Assigned To* dropdown or leave a comment`,
                        '',
                        `💡 *Tips:*`,
                        `• 🟢 Green rows = high confidence answers`,
                        `• 🟡 Yellow rows = medium confidence — worth a closer look`,
                        `• 🟠 Orange rows = low confidence — likely needs editing`,
                        `• Check the *Gap Notes* column for any concerns the AI flagged`,
                        '',
                        `The AI weights previous submitted RFP samples higher than knowledge base content, so many answers should be close to final. Thanks team! 🙏`,
                      )

                      const msg = lines.join('\n')
                      navigator.clipboard.writeText(msg)
                      showToast('Slack message copied to clipboard', 'success')
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    <Copy size={13} />
                    Copy Slack Message
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })()}

      {/* Company Overview — always visible */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden p-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">
            <Info size={12} className="text-sky-400" />
            Company Overview
          </label>
          {project.company_overview?.trim() && (
            <button onClick={() => { navigator.clipboard.writeText(project.company_overview!); showToast('Copied', 'success') }} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" title="Copy to clipboard">
              <Copy size={12} />
            </button>
          )}
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mb-2">
          What does the company do, their industry, and scale. AI Assist can fill this in automatically.
        </p>
        <textarea
          value={project.company_overview || ''}
          onChange={(e) => {
            setProject({ ...project, company_overview: e.target.value || null })
          }}
          onBlur={() => {
            rfpApi.updateProject(projectId, { company_overview: project.company_overview || null })
          }}
          placeholder="e.g. Tyro Payments is an Australian payments company providing EFTPOS, business loans, and banking services to SMEs. They process ~$35B annually across 70,000+ merchants."
          rows={3}
          className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-sky-500 transition-colors resize-none"
        />
      </div>

      {/* Regulatory Environment — always visible */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden p-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">
            <Info size={12} className="text-sky-400" />
            Regulatory Environment
          </label>
          {project.regulatory_environment?.trim() && (
            <button onClick={() => { navigator.clipboard.writeText(project.regulatory_environment!); showToast('Copied', 'success') }} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" title="Copy to clipboard">
              <Copy size={12} />
            </button>
          )}
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mb-2">
          Regulatory bodies, frameworks, and compliance obligations. Passed to the Brain for context-aware responses.
        </p>
        <textarea
          value={project.regulatory_environment || ''}
          onChange={(e) => {
            setProject({ ...project, regulatory_environment: e.target.value || null })
          }}
          onBlur={() => {
            rfpApi.updateProject(projectId, { regulatory_environment: project.regulatory_environment || null })
          }}
          placeholder="e.g. APRA-regulated ADI, AUSTRAC reporting entity, subject to AML/CTF Act. AFSL holder regulated by ASIC."
          rows={2}
          className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-sky-500 transition-colors resize-none"
        />
      </div>

      {/* Industry Context — always visible */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden p-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">
            <Info size={12} className="text-sky-400" />
            Industry Context
          </label>
          {project.industry_context?.trim() && (
            <button onClick={() => { navigator.clipboard.writeText(project.industry_context!); showToast('Copied', 'success') }} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" title="Copy to clipboard">
              <Copy size={12} />
            </button>
          )}
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mb-2">
          Industry vertical and domain-specific language for responses. Ensures the AI uses the right terminology for this customer.
        </p>
        <textarea
          value={project.industry_context || ''}
          onChange={(e) => {
            setProject({ ...project, industry_context: e.target.value || null })
          }}
          onBlur={() => {
            rfpApi.updateProject(projectId, { industry_context: project.industry_context || null })
          }}
          placeholder='e.g. Superannuation / wealth management. Use "members" not "customers", "fund" not "company". Key use cases: member onboarding, rollover verification, death benefit claims.'
          rows={2}
          className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-sky-500 transition-colors resize-none"
        />
      </div>

      {/* RFP Details — collapsible section */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
        {/* Clickable header */}
        <div className="flex items-center justify-between w-full px-5 py-3.5 hover:bg-[var(--bg-tertiary)] transition-colors">
          <button
            onClick={() => setDetailsOpen(!detailsOpen)}
            className="flex items-center gap-3 flex-1 text-left"
          >
            {detailsOpen
              ? <ChevronDown size={15} className="text-[var(--text-muted)]" />
              : <ChevronRight size={15} className="text-[var(--text-muted)]" />
            }
            <ClipboardList size={15} className="text-indigo-400" />
            <span className="text-sm font-semibold text-[var(--text-primary)]">RFP Details</span>
          </button>

          <div className="flex items-center gap-3 shrink-0">
            {/* Mini status pills when collapsed */}
            {!detailsOpen && (
              <div className="flex items-center gap-3 text-[11px] text-[var(--text-muted)]">
                {project.question_cutoff_date && (
                  <span className="flex items-center gap-1">
                    <Calendar size={11} className="text-amber-400" />
                    Cutoff: {formatDate(project.question_cutoff_date)}
                  </span>
                )}
                {project.response_due_date && (
                  <span className="flex items-center gap-1">
                    <Calendar size={11} className="text-red-400" />
                    Due: {formatDate(project.response_due_date)}
                  </span>
                )}
                {cl.presales_recommendation && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${RECOMMENDATION_CONFIG[cl.presales_recommendation].bg} ${RECOMMENDATION_CONFIG[cl.presales_recommendation].color}`}>
                    Recommendation: {RECOMMENDATION_CONFIG[cl.presales_recommendation].label}
                  </span>
                )}
              </div>
            )}

            {/* Progress badge */}
            <div className="flex items-center gap-2">
              <div className="w-20 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${progressPercent === 100 ? 'bg-green-500' : 'bg-indigo-500'}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className={`text-[10px] font-semibold tabular-nums ${
                progressPercent === 100 ? 'text-green-400' : progressPercent > 0 ? 'text-indigo-400' : 'text-[var(--text-muted)]'
              }`}>
                {progressPercent}%
              </span>
            </div>

            {/* AI Assist */}
            <div onClick={e => e.stopPropagation()}>
              <RfpAiAssist
                projectId={projectId}
                dealId={project.deal_id}
                checklist={cl}
                questionCutoffDate={project.question_cutoff_date}
                responseDueDate={project.response_due_date}
                companyOverview={project.company_overview ?? null}
                regulatoryEnvironment={project.regulatory_environment ?? null}
                industryContext={project.industry_context ?? null}
                requirementCount={requirements.length}
                onApplyChecklist={handleChecklistChange}
                onApplyDate={handleDateChange}
                onApplyProject={(field, value) => {
                  setProject({ ...project, [field]: value })
                  rfpApi.updateProject(projectId, { [field]: value })
                }}
              />
            </div>
          </div>
        </div>

        {/* Expanded content */}
        {detailsOpen && (
          <div className="border-t border-[var(--border-color)]">
            {/* Dates row */}
            <div className="grid grid-cols-2 gap-px bg-[var(--border-color)]">
              <div className="bg-[var(--bg-secondary)] p-4">
                <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
                  <Calendar size={12} className="text-amber-400" />
                  Question Cutoff Date
                </label>
                <input
                  type="date"
                  value={project.question_cutoff_date || ''}
                  onChange={(e) => handleDateChange('question_cutoff_date', e.target.value || null)}
                  className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>
              <div className="bg-[var(--bg-secondary)] p-4">
                <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
                  <Calendar size={12} className="text-red-400" />
                  Response Due Date
                </label>
                <input
                  type="date"
                  value={project.response_due_date || ''}
                  onChange={(e) => handleDateChange('response_due_date', e.target.value || null)}
                  className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500 transition-colors"
                />
              </div>
            </div>

            {/* Google Sheet URL */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
                <FileSpreadsheet size={12} className="text-green-400" />
                Google Sheet URL
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={project.sheet_url || ''}
                  onChange={(e) => {
                    const val = e.target.value || null
                    setProject({ ...project, sheet_url: val })
                  }}
                  onBlur={(e) => {
                    rfpApi.updateProject(projectId, { sheet_url: e.target.value || null })
                  }}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="flex-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors"
                />
                {project.sheet_url && (
                  <a href={project.sheet_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:underline whitespace-nowrap">Open</a>
                )}
              </div>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                Paste a Google Sheets URL to export to your own sheet instead of the default. Must be a native Google Sheet (not an uploaded .xlsx).
                Share the sheet with <button type="button" onClick={() => { navigator.clipboard.writeText('dealhub-presales-service-accou@presales-dealhub-calendar.iam.gserviceaccount.com'); showToast('Service account email copied', 'success') }} className="text-blue-400 hover:underline font-mono">dealhub-presales-service-accou@presales-dealhub-calendar.iam.gserviceaccount.com</button> (Editor access).
              </p>
            </div>

            {/* Slack Channel */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
                <Bell size={12} className="text-blue-400" />
                Slack Channel ID
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={project.slack_channel_id || ''}
                  onChange={(e) => {
                    const val = e.target.value || null
                    setProject({ ...project, slack_channel_id: val })
                  }}
                  onBlur={(e) => {
                    rfpApi.updateProject(projectId, { slack_channel_id: e.target.value || null })
                  }}
                  placeholder="C0123456789"
                  className="flex-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors font-mono"
                />
                <span className="text-[10px] text-[var(--text-muted)]">Find in Slack channel settings</span>
              </div>
              <p className="text-[10px] text-amber-400/80 mt-1.5">Make sure the DealHub bot is invited to the channel — type <code className="bg-[var(--bg-tertiary)] px-1 rounded">/invite @DealHub</code> in Slack</p>
            </div>

            {/* Products */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <p className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">
                <Layers size={12} className="text-indigo-400" />
                Products
              </p>
              <div className="flex flex-wrap gap-1.5">
                {RFP_PRODUCTS.map(product => {
                  const selected = (cl.products || []).includes(product.key)
                  return (
                    <button
                      key={product.key}
                      onClick={() => {
                        const current = cl.products || []
                        const next = selected
                          ? current.filter(p => p !== product.key)
                          : [...current, product.key]
                        handleChecklistChange({ ...cl, products: next })
                      }}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-md border transition-all ${
                        selected
                          ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400 shadow-sm'
                          : 'border-[var(--border-color)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-secondary)]'
                      }`}
                    >
                      {product.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Qualification checklist */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <p className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">
                <Layers size={12} className="text-blue-400" />
                Qualification Checklist
              </p>
              <div className="space-y-2">
                {QUALIFICATION_CRITERIA.map(criterion => {
                  const val = cl[criterion.key]
                  return (
                    <div key={criterion.key} className="flex items-center justify-between gap-4 py-1.5 px-3 rounded-lg hover:bg-[var(--bg-primary)] transition-colors">
                      <p className="text-[13px] text-[var(--text-secondary)] flex-1">{criterion.label}</p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {(['yes', 'no'] as const).map(answer => {
                          const cfg = YES_NO_CONFIG[answer]
                          const selected = val === answer
                          return (
                            <button
                              key={answer}
                              onClick={() => {
                                const next: QualYesNo = selected ? null : answer
                                handleChecklistChange({ ...cl, [criterion.key]: next })
                              }}
                              className={`px-3 py-1 text-[11px] font-medium rounded-md border transition-all ${
                                selected
                                  ? `${cfg.bg} ${cfg.color} shadow-sm`
                                  : 'border-[var(--border-color)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-secondary)]'
                              }`}
                            >
                              {cfg.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Competitors */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
                <Users size={12} className="text-violet-400" />
                Competitors
              </label>
              <textarea
                value={cl.competitors}
                onChange={(e) => handleChecklistChange({ ...cl, competitors: e.target.value })}
                placeholder="Who else is in this RFP? e.g. Onfido (incumbent), Jumio (challenger), Sumsub..."
                rows={2}
                className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
              />
            </div>

            {/* Response Strategy */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1.5">
                <Sparkles size={12} className="text-violet-400" />
                AI Response Strategy
              </label>
              <p className="text-[11px] text-[var(--text-muted)] mb-2">
                Guide the tone and approach the AI uses when generating answers. This is applied to every AI-generated response for this RFP.
              </p>
              <textarea
                value={project.response_strategy || ''}
                onChange={(e) => {
                  setProject({ ...project, response_strategy: e.target.value || null })
                }}
                onBlur={() => {
                  rfpApi.updateProject(projectId, { response_strategy: project.response_strategy || null })
                }}
                placeholder="e.g. Be concise and compliance-focused. Emphasise our multi-vendor orchestration and Australian data residency. Avoid mentioning features still in beta. Use formal tone suitable for a banking RFP."
                rows={3}
                className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-violet-500 transition-colors resize-none"
              />
            </div>

            {/* Frankie Brain toggle */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <div>
                  <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    <BrainCircuit size={12} className="text-pink-400" />
                    Frankie Brain Mode
                  </label>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">
                    When enabled, each requirement also gets a second answer from Frankie Brain (runs as Pass 4 after AI search).
                  </p>
                </div>
                <button
                  onClick={() => {
                    const next = !project.brain_enabled
                    setProject({ ...project, brain_enabled: next })
                    rfpApi.updateProject(projectId, { brain_enabled: next })
                    showToast(next ? 'Frankie Brain enabled' : 'Frankie Brain disabled', 'success')
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${project.brain_enabled ? 'bg-pink-500' : 'bg-[var(--bg-primary)]'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${project.brain_enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            {/* Brain Review shadow mode */}
            {project.brain_enabled && (
            <div className="p-4 border-t border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <div>
                  <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    <Eye size={12} className="text-emerald-400" />
                    Brain Review Shadow Mode
                  </label>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">
                    Brain Review still runs and records its verdict, fit, confidence and gap notes, but leaves the final response and review status untouched. Use it to check what the review pass would do before letting it write.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const next = !project.brain_review_shadow
                    setProject({ ...project, brain_review_shadow: next })
                    rfpApi.updateProject(projectId, { brain_review_shadow: next })
                    showToast(next ? 'Brain Review shadow mode enabled' : 'Brain Review shadow mode disabled', 'success')
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${project.brain_review_shadow ? 'bg-emerald-500' : 'bg-[var(--bg-primary)]'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${project.brain_review_shadow ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
            )}

            {/* Question Granularity */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <div>
                  <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    <Layers size={12} className="text-amber-400" />
                    Question Granularity
                  </label>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">
                    When enabled, multi-part requirements are decomposed into standalone sub-questions before AI processing. Each sub-question is answered independently for better coverage.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const next = !project.question_granularity
                    setProject({ ...project, question_granularity: next })
                    rfpApi.updateProject(projectId, { question_granularity: next })
                    showToast(next ? 'Question granularity enabled' : 'Question granularity disabled', 'success')
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${project.question_granularity ? 'bg-amber-500' : 'bg-[var(--bg-primary)]'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${project.question_granularity ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            {/* Consolidate Mode */}
            {project.question_granularity && (
            <div className="p-4 border-t border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <div>
                  <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    <GitMerge size={12} className="text-orange-400" />
                    Consolidate Responses
                  </label>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">
                    After all passes complete, merges decomposed sub-question responses back into a single response per parent requirement. Reduces the review set from hundreds of sub-questions to the original requirement count.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const next = !project.consolidate_enabled
                    setProject({ ...project, consolidate_enabled: next })
                    rfpApi.updateProject(projectId, { consolidate_enabled: next })
                    showToast(next ? 'Consolidation enabled' : 'Consolidation disabled', 'success')
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${project.consolidate_enabled ? 'bg-orange-500' : 'bg-[var(--bg-primary)]'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${project.consolidate_enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
            )}

            {/* Self-Evaluation Mode */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <div>
                  <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    <ClipboardList size={12} className="text-cyan-400" />
                    Self-Evaluation Mode
                  </label>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">
                    When enabled, the AI rates each response (e.g. "Full Match", "Partial Match", "No Match"). Configure the exact labels below.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const next = !project.self_eval_enabled
                    const opts = next && !project.self_eval_options?.length ? SELF_EVAL_PRESETS[0].options : project.self_eval_options
                    setProject({ ...project, self_eval_enabled: next, self_eval_options: opts })
                    rfpApi.updateProject(projectId, { self_eval_enabled: next, self_eval_options: opts })
                    showToast(next ? 'Self-evaluation enabled' : 'Self-evaluation disabled', 'success')
                  }}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${project.self_eval_enabled ? 'bg-cyan-500' : 'bg-[var(--bg-primary)]'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${project.self_eval_enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>

              {project.self_eval_enabled && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-[var(--text-muted)] font-medium shrink-0">Presets:</span>
                    {SELF_EVAL_PRESETS.map(preset => {
                      const isActive = JSON.stringify(project.self_eval_options) === JSON.stringify(preset.options)
                      return (
                        <button
                          key={preset.label}
                          onClick={() => {
                            setProject({ ...project, self_eval_options: preset.options })
                            rfpApi.updateProject(projectId, { self_eval_options: preset.options })
                          }}
                          className={`px-2 py-1 text-[10px] rounded-md border transition-colors ${
                            isActive
                              ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400'
                              : 'border-[var(--border-color)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
                          }`}
                        >
                          {preset.label}
                        </button>
                      )
                    })}
                  </div>
                  <div>
                    <label className="text-[10px] text-[var(--text-muted)] font-medium block mb-1">Options (one per line)</label>
                    <textarea
                      value={(project.self_eval_options || []).join('\n')}
                      onChange={e => {
                        const opts = e.target.value.split('\n').map(s => s.trim()).filter(Boolean)
                        setProject({ ...project, self_eval_options: opts })
                      }}
                      onBlur={() => {
                        rfpApi.updateProject(projectId, { self_eval_options: project.self_eval_options || [] })
                      }}
                      rows={3}
                      placeholder="Full Match&#10;Partial Match&#10;No Match"
                      className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-cyan-500 transition-colors resize-none font-mono"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* AS-IS State — structured fields */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <p className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">
                <Layers size={12} className="text-cyan-400" />
                AS-IS State
              </p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--text-secondary)] mb-1.5">
                      Current Vendor
                      <Tooltip content="Who provides their current identity/fraud solution? This could be a direct competitor, an in-house build, or they may have nothing in place. Knowing the incumbent tells you the switching cost and whether the RFP is a genuine evaluation or a renewal exercise.">
                        <Info size={11} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                      </Tooltip>
                    </label>
                    <input
                      type="text"
                      value={cl.current_vendor}
                      onChange={(e) => handleChecklistChange({ ...cl, current_vendor: e.target.value })}
                      placeholder="e.g. Onfido, built in-house, none"
                      className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--text-secondary)] mb-1.5">
                      Current Solution
                      <Tooltip content="What capability does the customer actually have today? Are they doing basic KYC only, or do they have a full orchestration stack? Understanding the breadth of their current setup helps assess how much of FrankieOne's platform they'd actually use — and whether the migration is realistic.">
                        <Info size={11} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                      </Tooltip>
                    </label>
                    <input
                      type="text"
                      value={cl.current_solution}
                      onChange={(e) => handleChecklistChange({ ...cl, current_solution: e.target.value })}
                      placeholder="e.g. Basic KYC only, manual review process"
                      className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--text-secondary)] mb-1.5">
                    Pain Points / Gaps
                    <Tooltip content={<div className="space-y-1.5"><p>What's driving the customer to look for something new? Think about:</p><ul className="list-disc pl-3 space-y-0.5"><li>Coverage gaps — countries or document types not supported</li><li>Speed issues — slow verification times, manual review bottlenecks</li><li>Compliance pressure — new regulations they can't meet with current setup</li><li>Integration complexity — hard to maintain, poor API/SDK experience</li><li>Vendor relationship — poor support, lack of roadmap transparency</li><li>Cost — paying too much for what they get</li></ul><p className="text-amber-300 mt-1">If the customer is happy with the incumbent, this may be a compliance exercise and we're a stalking horse.</p></div>}>
                      <Info size={11} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                    </Tooltip>
                  </label>
                  <textarea
                    value={cl.pain_points}
                    onChange={(e) => handleChecklistChange({ ...cl, pain_points: e.target.value })}
                    placeholder="What's not working? Coverage gaps, speed issues, compliance pressure..."
                    rows={4}
                    className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors resize-y min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--text-secondary)] mb-1.5">
                    Reason for Change
                    <Tooltip content={<div className="space-y-1.5"><p>Why is the customer going to market right now? This is critical for qualifying the opportunity:</p><ul className="list-disc pl-3 space-y-0.5"><li><strong>Genuine dissatisfaction</strong> — actively unhappy with incumbent, real intent to switch</li><li><strong>Mandated refresh</strong> — procurement policy requires periodic review (e.g. every 3 years). Lower win probability if incumbent is performing.</li><li><strong>New regulation</strong> — regulatory change forces capability upgrade</li><li><strong>Leadership change</strong> — new CTO/CISO wants to re-evaluate stack</li><li><strong>Growth / expansion</strong> — entering new markets, need broader coverage</li></ul><p className="text-amber-300 mt-1">Switching costs are real. Even a better product loses to "good enough and already integrated" most of the time.</p></div>}>
                      <Info size={11} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                    </Tooltip>
                  </label>
                  <textarea
                    value={cl.reason_for_change}
                    onChange={(e) => handleChecklistChange({ ...cl, reason_for_change: e.target.value })}
                    placeholder="Why going to market? Genuine dissatisfaction, mandated refresh, new regulation..."
                    rows={4}
                    className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors resize-y min-h-[80px]"
                  />
                </div>
              </div>
            </div>

            {/* Salesperson */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <div className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
                <div className="flex items-center gap-2">
                  <Users size={12} className="text-amber-400" />
                  <span className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">Salesperson</span>
                </div>
                <select
                  value={project.owner_id ? String(project.owner_id) : ''}
                  onChange={e => handleOwnerChange(e.target.value ? parseInt(e.target.value) : null)}
                  className="flex-1 max-w-[220px] rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                >
                  <option value="">Not assigned</option>
                  {teamMembers.filter(m => m.expertise_areas.includes('Sales')).length > 0
                    ? teamMembers.filter(m => m.expertise_areas.includes('Sales')).map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))
                    : teamMembers.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))
                  }
                </select>
              </div>

              {/* Pre-sales Owner */}
              <div className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] mt-2">
                <div className="flex items-center gap-2">
                  <Users size={12} className="text-indigo-400" />
                  <span className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider">Pre-sales</span>
                </div>
                <select
                  value={project.presales_owner_id ? String(project.presales_owner_id) : ''}
                  onChange={e => handlePresalesOwnerChange(e.target.value ? parseInt(e.target.value) : null)}
                  className="flex-1 max-w-[220px] rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                >
                  <option value="">Not assigned</option>
                  {teamMembers.filter(m => !m.expertise_areas.includes('Sales')).length > 0
                    ? teamMembers.filter(m => !m.expertise_areas.includes('Sales')).map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))
                    : teamMembers.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))
                  }
                </select>
              </div>
            </div>

            {/* Pre-sales recommendation */}
            <div className="p-4 border-t border-[var(--border-color)]">
              <p className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1">
                <ThumbsUp size={12} className="text-green-400" />
                Pre-sales Recommendation
              </p>
              <p className="text-[11px] text-[var(--text-muted)] mb-3">
                Based on the qualification checklist, competitive landscape, and AS-IS state — is this RFP a good use of FrankieOne's resources? The default answer is <strong className="text-red-400">no</strong> unless ICP fit is confirmed, risks are manageable, and we have a credible right to win.
              </p>
              <div className="flex items-center gap-2">
                {(['yes', 'maybe', 'no'] as const).map(answer => {
                  const cfg = RECOMMENDATION_CONFIG[answer]
                  const selected = cl.presales_recommendation === answer
                  return (
                    <button
                      key={answer}
                      onClick={() => {
                        const next: PresalesRecommendation = selected ? null : answer
                        handleChecklistChange({ ...cl, presales_recommendation: next })
                      }}
                      className={`px-5 py-2 text-sm font-medium rounded-lg border transition-all ${
                        selected
                          ? `${cfg.bg} ${cfg.color} shadow-sm`
                          : 'border-[var(--border-color)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-secondary)]'
                      }`}
                    >
                      {cfg.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RFP Documents */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
        <div className="flex items-center justify-between w-full px-5 py-3.5 hover:bg-[var(--bg-tertiary)] transition-colors">
          <button
            onClick={() => setDocsOpen(!docsOpen)}
            className="flex items-center gap-3 flex-1 text-left"
          >
            {docsOpen
              ? <ChevronDown size={15} className="text-[var(--text-muted)]" />
              : <ChevronRight size={15} className="text-[var(--text-muted)]" />
            }
            <FileText size={15} className="text-blue-400" />
            <span className="text-sm font-semibold text-[var(--text-primary)]">RFP Documents</span>
            {projectDocs.length > 0 && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">
                {projectDocs.length} {projectDocs.length === 1 ? 'document' : 'documents'}
              </span>
            )}
          </button>
          <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => { setAddingDoc(true); setDocsOpen(true) }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors"
            >
              <Plus size={12} />
              Add Document
            </button>
          </div>
        </div>

        {docsOpen && (
          <div className="border-t border-[var(--border-color)]">
            <div className="p-4">
              <p className="text-[11px] text-[var(--text-muted)] mb-3">
                Attach the RFP document content here — introductory sections, scope descriptions, evaluation criteria, timelines, and any other context from the RFP that sits outside the requirements spreadsheet. This content is used when generating questions.
              </p>

              {/* Add document form */}
              {addingDoc && (
                <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 mb-3 space-y-2">
                  <input
                    type="text"
                    value={newDocTitle}
                    onChange={e => setNewDocTitle(e.target.value)}
                    placeholder="Document title (e.g. 'RFP Introduction', 'Evaluation Criteria')"
                    className="w-full rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-blue-500"
                  />
                  <textarea
                    value={newDocContent}
                    onChange={e => setNewDocContent(e.target.value)}
                    placeholder="Paste the document content here..."
                    rows={8}
                    className="w-full rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-blue-500 resize-y font-mono"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => { setAddingDoc(false); setNewDocTitle(''); setNewDocContent('') }}
                      className="px-2.5 py-1.5 text-xs rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (!newDocTitle.trim() || !newDocContent.trim()) {
                          showToast('Title and content are required', 'error')
                          return
                        }
                        setSavingDoc(true)
                        try {
                          await rfpApi.addProjectDocument(projectId, { title: newDocTitle.trim(), content_text: newDocContent.trim() })
                          const res = await rfpApi.listProjectDocuments(projectId)
                          setProjectDocs(unwrapList(res) as typeof projectDocs)
                          setAddingDoc(false)
                          setNewDocTitle('')
                          setNewDocContent('')
                          showToast('Document added', 'success')
                        } catch {
                          showToast('Failed to save document', 'error')
                        }
                        setSavingDoc(false)
                      }}
                      disabled={savingDoc || !newDocTitle.trim() || !newDocContent.trim()}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50"
                    >
                      {savingDoc ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                      Save
                    </button>
                  </div>
                </div>
              )}

              {/* Document list */}
              {projectDocs.length === 0 && !addingDoc ? (
                <div className="text-center py-6 rounded-lg border border-dashed border-[var(--border-color)]">
                  <FileText size={20} className="mx-auto mb-2 text-[var(--text-muted)]" />
                  <p className="text-xs text-[var(--text-muted)]">
                    No documents attached yet. Add the RFP document content so the AI can reference it when generating questions.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {projectDocs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-2.5 rounded-lg border bg-[var(--bg-primary)] border-[var(--border-color)]">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <FileText size={14} className="text-blue-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[var(--text-primary)] truncate">{doc.title}</p>
                          <p className="text-[10px] text-[var(--text-muted)]">
                            {Math.round(doc.content_length / 1024)} KB
                            {doc.created_by_name ? ` — by ${doc.created_by_name}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={async () => {
                            if (viewingDocId === doc.id) {
                              setViewingDocId(null)
                              setViewingDocContent(null)
                              return
                            }
                            const res = await rfpApi.getProjectDocument(doc.id)
                            const d = unwrap(res)
                            if (d?.content_text) {
                              setViewingDocId(doc.id)
                              setViewingDocContent(d.content_text)
                            }
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                        >
                          {viewingDocId === doc.id ? <X size={10} /> : <Eye size={10} />}
                          {viewingDocId === doc.id ? 'Close' : 'View'}
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete "${doc.title}"?`)) return
                            await rfpApi.deleteProjectDocument(doc.id)
                            setProjectDocs(prev => prev.filter(d => d.id !== doc.id))
                            if (viewingDocId === doc.id) { setViewingDocId(null); setViewingDocContent(null) }
                            showToast('Document deleted', 'success')
                          }}
                          className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Document viewer */}
              {viewingDocId && viewingDocContent && (
                <div className="mt-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
                  <pre className="text-[11px] text-[var(--text-secondary)] whitespace-pre-wrap font-mono max-h-60 overflow-y-auto">
                    {viewingDocContent}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Questions */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
        <div className="flex items-center justify-between w-full px-5 py-3.5 hover:bg-[var(--bg-tertiary)] transition-colors">
          <button
            onClick={() => setQuestionsOpen(!questionsOpen)}
            className="flex items-center gap-3 flex-1 text-left"
          >
            {questionsOpen
              ? <ChevronDown size={15} className="text-[var(--text-muted)]" />
              : <ChevronRight size={15} className="text-[var(--text-muted)]" />
            }
            <MessageCircleQuestion size={15} className="text-amber-400" />
            <span className="text-sm font-semibold text-[var(--text-primary)]">Questions</span>
            {qualQuestions && (
              <>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">
                  {qualQuestions.groups.reduce((acc, g) => acc + g.questions.length, 0)} questions
                </span>
              </>
            )}
          </button>

          <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
            {qualQuestions && (
              <button
                onClick={handleExportQuestionsCsv}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                <Download size={12} />
                Export CSV
              </button>
            )}
            <button
              onClick={handleGenerateQuestions}
              disabled={generatingQuestions || !project.total_requirements}
              title={!project.total_requirements ? 'Upload an RFP document first' : undefined}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50"
            >
              {generatingQuestions
                ? <><Loader2 size={12} className="animate-spin" />Analysing...</>
                : <><Sparkles size={12} />{qualQuestions ? 'Regenerate' : 'Generate'}</>
              }
            </button>
          </div>
        </div>

        {questionsOpen && (
          <div className="border-t border-[var(--border-color)]">
            <div className="p-4">
              <p className="text-[11px] text-[var(--text-muted)] mb-3">
                AI analyses your qualification data and suggests strategic questions to submit during the RFP Q&amp;A period. These are professionally worded for the RFP issuer, but designed to help pre-sales assess fit and strengthen our response.
              </p>
              <div>
                <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1.5 block">Additional context (optional)</label>
                <textarea
                  value={qualNotes}
                  onChange={e => setQualNotes(e.target.value)}
                  placeholder="Any intel that should inform the questions — e.g. what the AE has shared, partner feedback, known pain points, competitive dynamics..."
                  rows={2}
                  className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
                />
              </div>

              {/* Prompt Editor */}
              <div className="mt-3">
                <button
                  onClick={() => setPromptOpen(!promptOpen)}
                  className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                >
                  <Settings size={11} />
                  {promptOpen ? 'Hide' : 'Edit'} AI Prompt
                  {project?.questions_prompt && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400">Custom</span>}
                </button>
                {promptOpen && (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={promptText}
                      onChange={e => setPromptText(e.target.value)}
                      rows={12}
                      className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-xs font-mono text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-indigo-500 transition-colors resize-y"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={async () => {
                          setSavingPrompt(true)
                          const customValue = promptText.trim() === defaultPrompt.trim() ? null : promptText.trim()
                          await rfpApi.updateProject(projectId, { questions_prompt: customValue } as any)
                          setProject(prev => prev ? { ...prev, questions_prompt: customValue } : prev)
                          showToast(customValue ? 'Custom prompt saved' : 'Reset to default prompt', 'success')
                          setSavingPrompt(false)
                        }}
                        disabled={savingPrompt}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
                      >
                        {savingPrompt ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                        Save Prompt
                      </button>
                      {promptText.trim() !== defaultPrompt.trim() && (
                        <button
                          onClick={() => {
                            setPromptText(defaultPrompt)
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                        >
                          <RotateCcw size={11} />
                          Reset to Default
                        </button>
                      )}
                      {project?.questions_prompt && (
                        <span className="text-[10px] text-amber-400">Using custom prompt</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {qualQuestions && (
              <div className="border-t border-[var(--border-color)] p-4 space-y-4">
                {/* Cassidy Questions */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={13} className="text-amber-400" />
                    <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">Cassidy Questions</span>
                  </div>
                  <div className="rounded-lg bg-amber-500/5 border border-amber-500/10 px-4 py-3 mb-3">
                    <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{qualQuestions.summary}</p>
                  </div>
                  {qualQuestions.groups.map((group, gi) => (
                    <div key={gi} className="mb-3">
                      <p className="text-[11px] font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">{group.theme}</p>
                      <div className="space-y-2">
                        {group.questions.map((q, qi) => (
                          <div key={qi} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3 group/q relative">
                            <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover/q:opacity-100 transition-all">
                              <button
                                onClick={() => { navigator.clipboard.writeText(q.question); showToast('Copied to clipboard', 'info') }}
                                className="p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                                title="Copy question"
                              >
                                <Copy size={12} />
                              </button>
                              <button
                                onClick={() => handleRemoveQuestion(gi, qi)}
                                className="p-1 rounded hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                                title="Remove question"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                            <p className="text-[13px] text-[var(--text-primary)] font-medium pr-12">{q.question}</p>
                            <p className="text-[11px] text-[var(--text-muted)] mt-1.5 italic">Strategy: {q.why}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* Question Debug Logs */}
            <div className="border-t border-[var(--border-color)]">
              <button
                onClick={() => setQuestionLogsOpen(!questionLogsOpen)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Terminal size={13} />
                  Debug Logs
                  {(() => {
                    const qLogs = logs.filter(l => l.pass === 'questions')
                    const errCount = qLogs.filter(l => l.level === 'error').length
                    return qLogs.length > 0 ? (
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--bg-tertiary)]">
                        {errCount > 0 ? `${errCount} errors` : `${qLogs.length} entries`}
                      </span>
                    ) : null
                  })()}
                </span>
                {questionLogsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {questionLogsOpen && (
                <div className="border-t border-[var(--border-color)]">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
                    <span className="text-[10px] text-[var(--text-muted)]">Showing question generation logs</span>
                    <button
                      onClick={fetchLogs}
                      className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      Refresh
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto bg-[#0d1117] font-mono text-[11px]">
                    {(() => {
                      const qLogs = logs.filter(l => l.pass === 'questions')
                      return qLogs.length === 0 ? (
                        <p className="px-4 py-6 text-center text-[var(--text-muted)] text-xs">No question logs yet. Generate questions to see logs.</p>
                      ) : (
                        qLogs.map(log => (
                          <div
                            key={log.id}
                            className={`px-4 py-1 border-b border-white/5 flex items-start gap-3 ${
                              log.level === 'error' ? 'bg-red-500/5 text-red-400' :
                              log.level === 'warn' ? 'text-yellow-400' :
                              'text-gray-400'
                            }`}
                          >
                            <span className="text-[10px] text-gray-600 whitespace-nowrap shrink-0">
                              {new Date(log.created_at + 'Z').toLocaleTimeString()}
                            </span>
                            <span className={`text-[10px] font-bold uppercase shrink-0 w-8 ${
                              log.level === 'error' ? 'text-red-500' : log.level === 'warn' ? 'text-yellow-500' : 'text-green-500'
                            }`}>
                              {log.level === 'error' ? 'ERR' : log.level === 'warn' ? 'WRN' : 'OK'}
                            </span>
                            {log.provider && <span className="text-[10px] text-purple-400 shrink-0">[{log.provider}]</span>}
                            <span className="flex-1 break-all">{log.message}</span>
                            {log.duration_ms != null && (
                              <span className={`text-[10px] shrink-0 ${log.duration_ms > 10000 ? 'text-red-400' : log.duration_ms > 5000 ? 'text-yellow-400' : 'text-gray-600'}`}>
                                {(log.duration_ms / 1000).toFixed(1)}s
                              </span>
                            )}
                          </div>
                        ))
                      )
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Requirements */}
      {requirements.length === 0 ? (
        <ExcelUploader onImport={handleImport} disabled={importing} />
      ) : (
          <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5">
              <button
                onClick={() => setRequirementsOpen(!requirementsOpen)}
                className="flex items-center gap-3 text-left hover:opacity-80 transition-opacity"
              >
                {requirementsOpen ? <ChevronDown size={15} className="text-[var(--text-muted)]" /> : <ChevronRight size={15} className="text-[var(--text-muted)]" />}
                <Layers size={15} className="text-violet-400" />
                <span className="text-sm font-semibold text-[var(--text-primary)]">Requirements</span>
                <span className="text-[10px] text-[var(--text-muted)]">{requirements.length} total</span>
              </button>
              <button
                onClick={async () => {
                  const confirmed = window.confirm(
                    `This will delete all ${requirements.length} imported requirements and their AI responses. You can re-import afterwards.\n\nAre you sure?`
                  )
                  if (!confirmed) return
                  await rfpApi.deleteRequirements(projectId)
                  setRequirements([])
                  setProject(prev => prev ? { ...prev, total_requirements: 0, answered_count: 0, status: 'draft' as const, original_filename: null } : prev)
                  showToast('Requirements cleared — ready to re-import', 'success')
                }}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                title="Delete all imported requirements"
              >
                <Trash2 size={11} />
                Clear Import
              </button>
            </div>
            {requirementsOpen && (
              <div className="border-t border-[var(--border-color)] p-4">
                <RequirementsTable
                  requirements={requirements}
                  teamMembers={teamMembers}
                  hasReferences={references.length > 0}
                  onUpdate={loadAll}
                  selfEvalOptions={project.self_eval_enabled ? project.self_eval_options : null}
                />

                {/* Debug Logs */}
                <div className="border-t border-[var(--border-color)]">
                  <button
                    onClick={() => setLogsOpen(!logsOpen)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Terminal size={13} />
                      Debug Logs
                      {logs.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--bg-tertiary)]">
                          {logs.filter(l => l.level === 'error').length > 0
                            ? `${logs.filter(l => l.level === 'error').length} errors`
                            : `${logs.length} entries`}
                        </span>
                      )}
                    </span>
                    {logsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  {logsOpen && (
                    <div className="border-t border-[var(--border-color)]">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
                        <span className="text-[10px] text-[var(--text-muted)]">Auto-refreshes every 3s while open</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={fetchLogs}
                            className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                          >
                            Refresh
                          </button>
                          <button
                            onClick={async () => { await rfpApi.clearLogs(projectId); setLogs([]) }}
                            className="flex items-center gap-1 text-[10px] text-red-400 hover:text-red-300 transition-colors"
                          >
                            <Trash2 size={10} />
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="max-h-80 overflow-y-auto bg-[#0d1117] font-mono text-[11px]">
                        {logs.length === 0 ? (
                          <p className="px-4 py-6 text-center text-[var(--text-muted)] text-xs">No logs yet. Run AI to see processing logs.</p>
                        ) : (
                          logs.map(log => (
                            <div
                              key={log.id}
                              className={`px-4 py-1 border-b border-white/5 flex items-start gap-3 ${
                                log.level === 'error' ? 'bg-red-500/5 text-red-400' :
                                log.level === 'warn' ? 'text-yellow-400' :
                                'text-gray-400'
                              }`}
                            >
                              <span className="text-[10px] text-gray-600 whitespace-nowrap shrink-0">
                                {new Date(log.created_at + 'Z').toLocaleTimeString()}
                              </span>
                              <span className={`text-[10px] font-bold uppercase shrink-0 w-8 ${
                                log.level === 'error' ? 'text-red-500' : log.level === 'warn' ? 'text-yellow-500' : 'text-green-500'
                              }`}>
                                {log.level === 'error' ? 'ERR' : log.level === 'warn' ? 'WRN' : 'OK'}
                              </span>
                              {log.pass && <span className="text-[10px] text-blue-400 shrink-0">[{log.pass}]</span>}
                              {log.requirement_id && <span className="text-[10px] text-gray-600 shrink-0">req:{log.requirement_id}</span>}
                              <span className="flex-1 break-all">{log.message}</span>
                              {log.duration_ms != null && (
                                <span className={`text-[10px] shrink-0 ${log.duration_ms > 10000 ? 'text-red-400' : log.duration_ms > 5000 ? 'text-yellow-400' : 'text-gray-600'}`}>
                                  {(log.duration_ms / 1000).toFixed(1)}s
                                </span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      }

      {/* Slack Reminders */}
      <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
        <button
          onClick={() => {
            const next = !slackRemindersOpen
            setSlackRemindersOpen(next)
            if (next && slackMessages.length === 0) {
              rfpApi.getSlackMessages(projectId).then(res => {
                const data = (res?.data as any)?.data ?? res?.data
                if (Array.isArray(data)) setSlackMessages(data)
              })
            }
          }}
          className="flex items-center justify-between w-full px-5 py-3.5 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
        >
          <div className="flex items-center gap-3">
            {slackRemindersOpen ? <ChevronDown size={15} className="text-[var(--text-muted)]" /> : <ChevronRight size={15} className="text-[var(--text-muted)]" />}
            <Bell size={15} className="text-blue-400" />
            <span className="text-sm font-semibold text-[var(--text-primary)]">Slack Reminders</span>
            {project.reminders_active ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Active</span>
            ) : (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--bg-tertiary)] text-[var(--text-muted)]">Inactive</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
            {slackMessages.length > 0 && (
              <span>
                {slackMessages.length} message{slackMessages.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </button>
        {slackRemindersOpen && (
          <div className="border-t border-[var(--border-color)] p-4 space-y-4">
            {/* Actions */}
            <div className="flex items-center gap-3 flex-wrap">
              {project.slack_channel_id && (
                <>
                  <button
                    onClick={async () => {
                      if (celebrateLoading) return
                      setCelebrateLoading('details')
                      try {
                        const res = await rfpApi.postDetails(projectId)
                        const d = (res?.data as any)?.data ?? res?.data
                        if (res.success && d?.posted) {
                          showToast('RFP details posted to Slack', 'success')
                          const msgsRes = await rfpApi.getSlackMessages(projectId)
                          const data = (msgsRes?.data as any)?.data ?? msgsRes?.data
                          if (Array.isArray(data)) setSlackMessages(data)
                        } else {
                          showToast(d?.slack_error || (res as any).error || 'Failed to post details', 'error')
                        }
                      } catch {
                        showToast('An error occurred', 'error')
                      }
                      setCelebrateLoading(null)
                    }}
                    disabled={!!celebrateLoading}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20 ${celebrateLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {celebrateLoading === 'details' ? <Loader2 size={12} className="animate-spin" /> : <ClipboardList size={12} />}
                    Post RFP Details
                  </button>

                  <button
                    onClick={async () => {
                      if (remindersLoading) return
                      setRemindersLoading(true)
                      try {
                        if (project.reminders_active) {
                          const res = await rfpApi.stopReminders(projectId)
                          if (res.success) {
                            setProject({ ...project, reminders_active: false })
                            showToast('Reminders stopped', 'success')
                          } else {
                            showToast((res as any).error || 'Failed to stop reminders', 'error')
                          }
                        } else {
                          if (!project.response_due_date) {
                            showToast('Set a Response Due Date in RFP Details first', 'error')
                            setRemindersLoading(false)
                            return
                          }
                          const res = await rfpApi.startReminders(projectId)
                          if (res.success) {
                            setProject({ ...project, reminders_active: true, reminders_started_at: new Date().toISOString() })
                            showToast('Reminders activated — first reminder posted to Slack', 'success')
                            const msgsRes = await rfpApi.getSlackMessages(projectId)
                            const data = (msgsRes?.data as any)?.data ?? msgsRes?.data
                            if (Array.isArray(data)) setSlackMessages(data)
                          } else {
                            showToast((res as any).error || 'Failed to start reminders', 'error')
                          }
                        }
                      } catch {
                        showToast('An error occurred', 'error')
                      }
                      setRemindersLoading(false)
                    }}
                    disabled={remindersLoading}
                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                      project.reminders_active
                        ? 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20'
                        : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                    } ${remindersLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {remindersLoading ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : project.reminders_active ? (
                      <BellOff size={12} />
                    ) : (
                      <Bell size={12} />
                    )}
                    {project.reminders_active ? 'Stop Reminders' : 'Start Reminders'}
                  </button>

                  {project.reminders_active && (
                    <button
                      onClick={async () => {
                        if (celebrateLoading) return
                        setCelebrateLoading('post-now')
                        try {
                          const res = await rfpApi.postReminderNow(projectId)
                          const d = (res?.data as any)?.data ?? res?.data
                          if (res.success && (d?.message_sent || (Array.isArray(d) && d[0]?.message_sent))) {
                            showToast('Reminder posted to Slack', 'success')
                            const msgsRes = await rfpApi.getSlackMessages(projectId)
                            const data = (msgsRes?.data as any)?.data ?? msgsRes?.data
                            if (Array.isArray(data)) setSlackMessages(data)
                          } else {
                            showToast((res as any).error || 'Failed to post reminder', 'error')
                          }
                        } catch {
                          showToast('An error occurred', 'error')
                        }
                        setCelebrateLoading(null)
                      }}
                      disabled={!!celebrateLoading}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all bg-violet-500/10 text-violet-400 border-violet-500/20 hover:bg-violet-500/20 ${celebrateLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {celebrateLoading === 'post-now' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Post Now
                    </button>
                  )}

                  <button
                    onClick={async () => {
                      if (celebrateLoading) return
                      setCelebrateLoading('completed')
                      try {
                        const res = await rfpApi.sendCelebration(projectId)
                        if (res.success) {
                          showToast('Completed message sent to Slack!', 'success')
                          const msgsRes = await rfpApi.getSlackMessages(projectId)
                          const data = (msgsRes?.data as any)?.data ?? msgsRes?.data
                          if (Array.isArray(data)) setSlackMessages(data)
                        } else {
                          showToast((res as any).error || 'Failed to send message', 'error')
                        }
                      } catch {
                        showToast('An error occurred', 'error')
                      }
                      setCelebrateLoading(null)
                    }}
                    disabled={!!celebrateLoading}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20 ${celebrateLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {celebrateLoading === 'completed' ? <Loader2 size={12} className="animate-spin" /> : <PartyPopper size={12} />}
                    Send Completed
                  </button>
                </>
              )}

              <div className="flex-1" />

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={project.reminder_images !== false}
                  onChange={(e) => {
                    const val = e.target.checked
                    setProject({ ...project, reminder_images: val })
                    rfpApi.updateProject(projectId, { reminder_images: val })
                  }}
                  className="w-3.5 h-3.5 rounded border-gray-300"
                />
                <span className="text-[11px] text-[var(--text-muted)]">Mood images</span>
              </label>
            </div>

            {/* Guide when no channel set */}
            {!project.slack_channel_id && (
              <div className="rounded-lg bg-[var(--bg-tertiary)] px-4 py-3 text-xs text-[var(--text-muted)]">
                Set a <span className="font-medium text-[var(--text-secondary)]">Slack Channel ID</span> in RFP Details above to enable posting.
              </div>
            )}

            {/* Status + Schedule */}
            <div className="text-xs text-[var(--text-muted)]">
              {project.reminders_active ? (
                <span>Active since {project.reminders_started_at ? new Date(project.reminders_started_at.replace(' ', 'T') + 'Z').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Melbourne' }) : 'now'}</span>
              ) : (
                <span>Reminders are not running</span>
              )}
              {project.slack_channel_id && (
                <span className="ml-2">· Channel: <code className="text-[10px]">{project.slack_channel_id}</code></span>
              )}
              {project.reminders_active && project.response_due_date && (() => {
                const dueDate = new Date(project.response_due_date + 'T00:00:00+10:00')
                const now = new Date()
                const daysLeft = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                const lastReminder = slackMessages.find(m => ['reminder', 'urgent', 'overdue'].includes(m.message_type))
                const lastSentAt = lastReminder ? new Date(lastReminder.created_at + 'Z') : null
                const hoursSinceLast = lastSentAt ? (now.getTime() - lastSentAt.getTime()) / (1000 * 60 * 60) : null

                let frequency: string
                let nextEstimate: string
                if (daysLeft < 0) {
                  frequency = 'Overdue — one final nudge then stops'
                  nextEstimate = lastReminder?.message_type === 'overdue' ? 'No more reminders (overdue already sent)' : 'Next check: tomorrow ~8 AM'
                } else if (daysLeft <= 2) {
                  frequency = 'Urgent — daily reminders'
                  nextEstimate = hoursSinceLast !== null && hoursSinceLast < 20 ? 'Sent today — next tomorrow ~8 AM' : 'Next check: tomorrow ~8 AM'
                } else if (daysLeft <= 5) {
                  frequency = 'Daily reminders'
                  nextEstimate = hoursSinceLast !== null && hoursSinceLast < 20 ? 'Sent today — next tomorrow ~8 AM' : 'Next check: tomorrow ~8 AM'
                } else {
                  frequency = 'Every 2 days'
                  nextEstimate = hoursSinceLast !== null && hoursSinceLast < 44 ? `Next in ~${Math.max(1, Math.ceil(44 - hoursSinceLast))} hours` : 'Next check: tomorrow ~8 AM'
                }

                const dueLabel = daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? 'Due today' : daysLeft === 1 ? 'Due tomorrow' : `${daysLeft}d left`
                return <span className="ml-2">· {dueLabel} · {frequency} · {nextEstimate}</span>
              })()}
            </div>

            {/* Bot invite warning for failed messages */}
            {slackMessages.some(m => !m.slack_ts) && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>Some messages failed to send. Make sure the DealHub bot is invited to the Slack channel — type <code className="bg-[var(--bg-tertiary)] px-1 rounded">/invite @DealHub</code> in Slack, then use Resend.</span>
              </div>
            )}

            {/* Message history */}
            {slackMessages.length > 0 ? (
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {slackMessages.map(msg => {
                  const typeColors: Record<string, string> = {
                    details: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
                    initial: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
                    reminder: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
                    urgent: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
                    overdue: 'bg-red-500/10 text-red-400 border-red-500/20',
                    complete: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
                  }
                  const tagged = msg.people_tagged ? JSON.parse(msg.people_tagged) as string[] : []
                  const failed = !msg.slack_ts
                  return (
                    <details key={msg.id} className={`rounded-lg border bg-[var(--bg-primary)] group ${failed ? 'border-red-500/30' : 'border-[var(--border-color)]'}`}>
                      <summary className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors text-xs">
                        <span className="text-[10px] text-[var(--text-muted)] tabular-nums whitespace-nowrap">
                          {new Date(msg.created_at + 'Z').toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Melbourne' })} AEST
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${typeColors[msg.message_type] || 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>
                          {msg.message_type}
                        </span>
                        {failed && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium border bg-red-500/10 text-red-400 border-red-500/20">
                            failed
                          </span>
                        )}
                        {tagged.length > 0 && (
                          <span className="text-[10px] text-[var(--text-muted)] truncate">
                            {tagged.join(', ')}
                          </span>
                        )}
                        <span className="flex-1 text-[var(--text-muted)] truncate text-[11px]">
                          {msg.message_text.slice(0, 80)}...
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (resendingId === msg.id) return
                            setResendingId(msg.id)
                            try {
                              const res = await rfpApi.resendSlackMessage(msg.id)
                              if (res.success && (res.data as any)?.resent) {
                                showToast('Message resent to Slack', 'success')
                                setSlackMessages(prev => prev.map(m => m.id === msg.id ? { ...m, slack_ts: (res.data as any).slack_ts } : m))
                              } else {
                                showToast('Failed to resend message', 'error')
                              }
                            } catch {
                              showToast('Failed to resend message', 'error')
                            }
                            setResendingId(null)
                          }}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors whitespace-nowrap"
                          title="Resend this message to Slack"
                        >
                          {resendingId === msg.id ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                          Resend
                        </button>
                      </summary>
                      <div className="px-3 pb-3 pt-1 border-t border-[var(--border-color)]">
                        <pre className="text-[11px] text-[var(--text-secondary)] whitespace-pre-wrap font-sans leading-relaxed">{msg.message_text}</pre>
                      </div>
                    </details>
                  )
                })}
              </div>
            ) : (
              <p className="text-xs text-[var(--text-muted)] text-center py-2">
                No messages yet.{!project.reminders_active && ' Start reminders to post an initial message.'}
              </p>
            )}
          </div>
        )}
      </div>

      <QualificationGuide
        open={showGuide}
        onClose={() => setShowGuide(false)}
        checklist={cl}
        onChecklistChange={handleChecklistChange}
      />
    </div>
  )
}
